import {useEffect, useRef, useState} from 'react'
import {Form, Link, useNavigate, useRouteLoaderData} from 'react-router'

import {useSignInHref} from '@core/auth-ui/signInHref'
import type {loader as rootLoader} from '@root'
import styles from './UserBlock.module.scss'

/** `phoneOnly` — a row that duplicates one of the header's own nav doors (Toile's Packs): shown only
 *  below 640px, where the shared `Header.module.scss` folds its `.nav` slot, so no width shows the
 *  door twice. Without it the row stands at every width — a door the header carries nowhere else
 *  (Narcis's "Your model", `/model` parked under this menu alone). */
export type UserBlockExtraLink = {href: string; label: string; phoneOnly?: boolean}

export type UserBlockProps = {
    /** Extra links in the signed-in menu, above the address divider — Toile's own Packs door;
     *  empty for a house with nothing else on the wall to send a signed-in reader to (Ovid). The
     *  first one still renders behind the `packsItem`/`divider` classes Toile's menu always used
     *  (kept literal rather than renamed now that the slot is generic — `UserBlock.module.scss`'s
     *  own header on why). The divider renders only alongside at least one link, so a house with
     *  none gets a plain address instead of a rule with nothing above it. */
    extraLinks?: UserBlockExtraLink[]
    /** The anonymous door: a link (Toile's `/auth/login`) or the wall's own sign-in layer (Ovid's
     *  `onSignIn`, `Wall.tsx`'s `renderHeader` second argument) — same pill, either behaviour. */
    signIn: {href: string} | {onSignIn: () => void}
}

/**
 * The header's right rail — thinking/design/toile_surfaces/Chrome.dc.html ("top of page" and
 * "signed in"). Lifted out of Toile's own `products/toile/components/UserBlock` (plan
 * `2026_09_17_ovid_one_wall.md`) so Ovid's member wall carries the same address-and-sign-out menu
 * rather than shipping with none, as its own fork of the header left it.
 *
 * Anonymous: the outlined "Sign in" pill (`signIn` decides what it does).
 * Signed in: the initial avatar with a menu holding `extraLinks`, the address and a sign-out form.
 *
 * The user comes from the shared root loader (`useRouteLoaderData('root')`) — the same way narcis's
 * own `UserBlock` reads it — the root loader runs `handleSession` for every product. The canvas's
 * menu also draws "Manage billing"; neither house that mounts this component sells anything from
 * the wall yet, so that row stays out rather than shipping inert.
 *
 * Packs (Toile's own `extraLinks` entry, plan `2026_09_04_wall_header_reveal.md` § Step 3) is
 * rendered at every width but hidden above 640px by `.module.scss`'s own media query (the SAME
 * 640px threshold the shared `Header.module.scss` hides its `.nav` slot below, so no width shows a
 * signed-in Toile reader the door twice) rather than unmounted, so it stays in the DOM/tab order at
 * desktop width too.
 *
 * Signed out on the phone, a house with a nav door carries none of it here at all (Toile's own call,
 * plan § Step 3's one reversible one): the Sign in pill cannot be a menu, and a text link beside it
 * leaves the field too narrow for its words.
 */
export function UserBlock({extraLinks = [], signIn}: UserBlockProps) {
    const loaderData = useRouteLoaderData<typeof rootLoader>('root')
    const user = loaderData?.user
    const [isOpen, setIsOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) return
        function handleOutsideClick(e: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [isOpen])

    if (!user) {
        return 'href' in signIn ? (
            <SignInLink loginPath={signIn.href} />
        ) : (
            <button type="button" className={styles.signIn} onClick={signIn.onSignIn}>
                Sign in
            </button>
        )
    }

    const initial = user.email ? user.email[0].toUpperCase() : '?'

    return (
        <div className={styles.menu} ref={wrapperRef}>
            <button
                type="button"
                className={styles.avatar}
                aria-label="Account menu"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((v) => !v)}
            >
                {initial}
            </button>
            {isOpen && (
                <AccountMenu extraLinks={extraLinks} email={user.email ?? ''} onNavigate={() => setIsOpen(false)} />
            )}
        </div>
    )
}

/** The anonymous pill as a link to the sign-in page, carrying this page as the way back. */
function SignInLink({loginPath}: {loginPath: string}) {
    const {href, live} = useSignInHref(loginPath)
    const navigate = useNavigate()
    return (
        <Link
            to={href}
            className={styles.signIn}
            onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                e.preventDefault()
                navigate(live())
            }}
        >
            Sign in
        </Link>
    )
}

/** The open dropdown's contents — stateless, so it can be exercised directly
 *  (`__tests__/UserBlock.spec.tsx`) without simulating the avatar click that mounts it in the
 *  browser (this tree runs no jsdom/testing-library — `@core/ui/useHeaderReveal`'s own header on
 *  why). `onNavigate` closes the menu on a link tap; the sign-out form posts and navigates on its
 *  own, so it carries none. */
export function AccountMenu({
    extraLinks, email, onNavigate,
}: {
    extraLinks: UserBlockExtraLink[]
    email: string
    onNavigate: () => void
}) {
    return (
        <div className={styles.dropdown}>
            {extraLinks.map((link) => (
                <Link
                    key={link.href}
                    to={link.href}
                    className={link.phoneOnly ? styles.packsItem : styles.menuItem}
                    onClick={onNavigate}
                >
                    {link.label}
                </Link>
            ))}
            {/* The rule follows the rows' own visibility: hidden with them above 640px when every
                row is phone-only, standing at every width once one row does. */}
            {extraLinks.length > 0 && (
                <hr className={extraLinks.every((l) => l.phoneOnly) ? styles.divider : styles.rule} />
            )}
            <p className={styles.email}>{email}</p>
            <Form method="POST" action="/auth/logout">
                <button type="submit" className={styles.item}>Sign out</button>
            </Form>
        </div>
    )
}
